import React from 'react';
import styled from 'styled-components';
import {
  ExperienceStatus,
  Targeting,
  TargetingDeviceType,
  Trigger,
} from '@/webapi/use-experience-api';
import { formatAudience } from '@/features/editor/widgets/shared/targeting-controls/audience-button';
import { formatEnum, truncate } from '@/utils/types';
import { Tooltip } from '@/components/tooltip';
import { getCaptionLabel } from '@/components/small-select';
import { QBItemSelection } from '@/components/query-builder/models';
import { HSpace } from '@/components/spacing';
import { currencies } from '@/utils/currencies';
import { maybe } from '@/features/details/utils';
import { formatTrigger } from '@/features/editor/widgets/shared/targeting-controls/trigger-button';

export function ExpStatusChip({
  bg,
  currentExperienceStatus,
  className,
}: {
  bg: string;
  currentExperienceStatus: ExperienceStatus;
  className?: string;
}) {
  let text = `${currentExperienceStatus}`;
  if (currentExperienceStatus === ExperienceStatus.PUBLISHED) {
    text = `live`;
  }
  return (
    <StatusChip className={className} bg={bg}>
      {text.toUpperCase()}
    </StatusChip>
  );
}

function placementShortDescription(targeting: Targeting) {
  return (formatEnum(targeting?.placement?.kind) || `ALL PAGES`).toLowerCase();
}

function formatValue(value: string | undefined) {
  return maybe(() => {
    const { pathname } = new URL(value);
    if (pathname === `/`) {
      return `homepage`;
    }
    return pathname;
  }, value);
}

function formatOp(op: string) {
  if (op === `isnot`) {
    return `is not`;
  }
  if (op === `notincludes`) {
    return `not includes`;
  }
  return op;
}

function And({ i }: { i: number }) {
  return (
    i !== 0 && (
      <>
        <Light>and</Light>
        <HSpace />
      </>
    )
  );
}

function Or({ i, len }: { i: number; len: number }) {
  return (
    i !== len - 1 && (
      <>
        <HSpace /> <Light>or</Light>
        <HSpace />
      </>
    )
  );
}

function FormattedValue({
  value: { currency, value },
}: {
  value: { op: string; value: string; currency?: string };
}) {
  return <span>{` ${currencies?.[currency] || ``}${formatValue(value)}`}</span>;
}

function selectionDescription(selection: QBItemSelection[]) {
  if (!selection || selection?.length === 0) {
    return null;
  }
  return (
    <Container>
      {selection?.map((x, i) => (
        <Column key={x.qbProps.caption}>
          <Row>
            <And i={i} />
            <span>{getCaptionLabel(x.qbProps?.caption)}</span>
            <HSpace />
            {x.values.map((y, i) => (
              <Row key={y.value}>
                <Light>{formatOp(y.op)}</Light>
                <HSpace />
                <FormattedValue value={y} />
                <Or i={i} len={x?.values?.length} />
              </Row>
            ))}
          </Row>
        </Column>
      ))}
    </Container>
  );
}

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
`;
const Column = styled.div`
  display: flex;
  flex-direction: column;
`;
const Container = styled.div`
  max-width: 350px;
`;
const Light = styled.span`
  opacity: 0.7;
`;

const TRIGGER_MAX_LENGTH = 15;

export function AudiencePreview({
  targeting,
  trigger,
}: {
  targeting: Targeting;
  trigger?: Trigger;
}) {
  const placementName = placementShortDescription(targeting);
  const audienceName = formatAudience(targeting, false);
  const audienceNameDetailed = formatAudience(targeting, true, 1000);

  const placementDetails = selectionDescription(targeting?.placement?.other);
  const audienceDescription = selectionDescription(targeting.other);
  const hideAudienceTooltip =
    audienceNameDetailed === audienceName && !audienceDescription;

  const triggerLabel = trigger ? formatTrigger(trigger) : null;

  return (
    <AudienceContainer>
      <AudienceElement
        noHover={!placementDetails}
        style={{
          marginLeft: `1rem`,
          paddingLeft: `1.5rem`,
          paddingRight: `1.5rem`,
        }}
      >
        <Tooltip
          zIndex={0}
          title=""
          width="auto"
          forceHide={!placementDetails}
          text={placementDetails}
          positions={[`bottom`]}
        >
          Pages:
          <MarginLeftSpan>{placementName}</MarginLeftSpan>
        </Tooltip>
      </AudienceElement>
      <AudienceElement
        noHover={hideAudienceTooltip}
        style={{
          paddingLeft: `1.5rem`,
          paddingRight: `1.5rem`,
        }}
      >
        <Tooltip
          zIndex={0}
          width="auto"
          title=""
          forceHide={hideAudienceTooltip}
          text={
            audienceDescription || (
              <Capitalize>{audienceNameDetailed}</Capitalize>
            )
          }
          positions={[`bottom`]}
        >
          Audience:
          <MarginLeftSpan>{audienceName}</MarginLeftSpan>
        </Tooltip>
      </AudienceElement>
      <AudienceElement
        noHover
        style={{
          paddingLeft: `1.5rem`,
          paddingRight: triggerLabel ? `1.5rem` : `0`,
        }}
      >
        Device:
        <MarginLeftSpan>
          {formatEnum(
            targeting.device
              ? TargetingDeviceType[targeting.device]
              : TargetingDeviceType.ALL_DEVICES,
          )?.toLowerCase()}
        </MarginLeftSpan>
      </AudienceElement>
      {triggerLabel && (
        <AudienceElement
          style={{
            paddingLeft: `1.5rem`,
            cursor:
              triggerLabel.length > TRIGGER_MAX_LENGTH ? `pointer` : `default`,
          }}
        >
          <Tooltip
            forceHide={triggerLabel.length <= TRIGGER_MAX_LENGTH}
            zIndex={0}
            title=""
            width="auto"
            text={triggerLabel}
            positions={[`bottom`]}
          >
            Trigger:
            <MarginLeftSpan>
              {truncate(triggerLabel, TRIGGER_MAX_LENGTH)}
            </MarginLeftSpan>
          </Tooltip>
        </AudienceElement>
      )}
    </AudienceContainer>
  );
}
const MarginLeftSpan = styled.span`
  margin-left: 1rem;
`;
const AudienceElement = styled.span<{
  noHover?: boolean;
}>`
  &:hover {
    cursor: ${(p) => (p.noHover ? `default` : `pointer`)};
    opacity: ${(p) => (p.noHover ? 1 : 0.8)};
  }
  border-left: 1px solid black;
`;

const Capitalize = styled.span`
  text-transform: capitalize;
`;
const AudienceContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  margin-left: 0.75rem;
  font-size: 1.2rem;
  color: black;
  font-weight: 400;
  font-family: Inter, serif;
  text-transform: capitalize;
`;
export const StatusChip = styled.p<{ bg: string }>`
  background-color: ${(p) => p.bg};
  border-radius: 3rem;
  padding: 0.4rem 1.2rem;
  color: white;
  user-select: none;
  font-family: Inter, serif;
  font-weight: 700;
  letter-spacing: 0.52px;
  font-size: 1.1rem;
  margin: 2px 0 0 0;
`;
